import { _isNotBlank, _notNil } from '@/littledash';
import type { UserApiId } from '@/model/User.model';
import { USER_GROUP_DESCRIPTION_MAX_LENGTH, USER_GROUP_NAME_MAX_LENGTH } from '@/model/UserGroups.model';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import type { EligibleMembers } from './useEligibleMembers';
import { useFormContext } from 'react-hook-form';
import UserGroupMemberPicker from './UserGroupMemberPicker';

export const NAME_ERROR_ID = 'user-group-name-error';
const DESCRIPTION_ERROR_ID = 'user-group-description-error';

export interface UserGroupFormValues {
  name: string;
  description: string;
  memberApiIds: Array<UserApiId>;
}

interface UserGroupFieldsProps {
  submitting: boolean;
  members: EligibleMembers;
}

/**
 * Name, description and member picker — the body shared by the create and edit group modals, which
 * present identical fields and differ only in how they save.
 */
const UserGroupFields: FC<UserGroupFieldsProps> = ({ submitting, members }) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<UserGroupFormValues>();

  const memberApiIds = watch('memberApiIds');

  return (
    <div data-test-component="UserGroupFields" data-test-element="container">
      <div className="pb3 ph4">
        <label htmlFor="user-group-name">Group name (required)</label>
        <input
          id="user-group-name"
          type="text"
          data-testid="name-input"
          style={{ marginBottom: 0 }}
          className={_notNil(errors.name) ? 'input__error' : ''}
          aria-invalid={_notNil(errors.name)}
          aria-describedby={_notNil(errors.name) ? NAME_ERROR_ID : undefined}
          {...register('name', {
            required: 'This field is required',
            validate: (value) => {
              if (!_isNotBlank(value)) {
                return 'This field is required';
              }
              return (
                value.trim().length <= USER_GROUP_NAME_MAX_LENGTH ||
                `Maximum of ${USER_GROUP_NAME_MAX_LENGTH} characters`
              );
            },
          })}
        />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => (
            <small id={NAME_ERROR_ID} role="alert" data-testid="name-error-message" className="red db pt2">
              {message}
            </small>
          )}
        />
      </div>

      <div className="pb3 ph4">
        <label htmlFor="user-group-description">Description</label>
        <input
          id="user-group-description"
          type="text"
          data-testid="description-input"
          style={{ marginBottom: 0 }}
          className={_notNil(errors.description) ? 'input__error' : ''}
          aria-invalid={_notNil(errors.description)}
          aria-describedby={_notNil(errors.description) ? DESCRIPTION_ERROR_ID : undefined}
          {...register('description', {
            validate: (value) =>
              value.trim().length <= USER_GROUP_DESCRIPTION_MAX_LENGTH ||
              `Maximum of ${USER_GROUP_DESCRIPTION_MAX_LENGTH} characters`,
          })}
        />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => (
            <small
              id={DESCRIPTION_ERROR_ID}
              role="alert"
              data-testid="description-error-message"
              className="red db pt2"
            >
              {message}
            </small>
          )}
        />
      </div>

      <div className="pb4 ph4" data-test-component="UserGroupFields" data-test-element="members">
        <UserGroupMemberPicker
          users={members.users}
          loading={members.loading}
          failed={members.failed}
          selected={memberApiIds}
          disabled={submitting}
          onRetry={members.retry}
          onChange={(selected) => setValue('memberApiIds', selected, { shouldDirty: true })}
        />
      </div>
    </div>
  );
};

export default UserGroupFields;
